import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import BasicInfoStep from './components/BasicInfoStep';
import DocumentVerificationStep from './components/DocumentVerificationStep';
import OwnerCertificationModal from './components/OwnerCertificationModal';
import { sendAdminSignupNotification } from '../../services/emailService';

const Signup = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showCertificationModal, setShowCertificationModal] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    accountType: 'renter',
    driverLicense: null,
    insuranceDocument: null,
    agreeToTerms: false,
    agreeToEligibility: false,
    ownerCertifications: null
  });

  const steps = [
    { number: 1, title: 'Basic Information', icon: 'User' },
    { number: 2, title: 'Document Verification', icon: 'FileCheck' }
  ];

  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleNext = () => {
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const uploadDocument = async (file, bucket, userId) => {
    if (!file) return null;

    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { data, error } = await supabase?.storage
      ?.from(bucket)
      ?.upload(filePath, file);

    if (error) throw error;

    return { filePath, fileName: file?.name };
  };

  const saveCertificationDocument = async (userId, certifications) => {
    try {
      const certificationText = `OWNER LISTING CERTIFICATION\n\nDate: ${new Date()?.toLocaleDateString()}\n\nI hereby certify the following:\n\n☑ I am the legal owner of this trailer or have full authority to rent it\n☑ The trailer is properly titled, registered, and roadworthy\n☑ I maintain any insurance required by law\n☑ I understand Draggin Trailers LLC does not insure or inspect my trailer\n☑ I agree to indemnify Draggin Trailers LLC from any claims arising from this listing\n\nUser ID: ${userId}\nCertified At: ${new Date()?.toISOString()}`;

      const blob = new Blob([certificationText], { type: 'text/plain' });
      const fileName = `certification-${Date.now()}.txt`;
      const filePath = `${userId}/${fileName}`;

      const { data, error } = await supabase?.storage
        ?.from('owner-certifications')
        ?.upload(filePath, blob);

      if (error) throw error;

      await supabase?.from('owner_certifications')?.insert({
        user_id: userId,
        legal_owner: certifications?.legalOwner,
        properly_titled: certifications?.properlyTitled,
        maintains_insurance: certifications?.maintainsInsurance,
        understands_no_inspection: certifications?.understandsNoInspection,
        agrees_indemnify: certifications?.agreesIndemnify,
        certification_document_path: filePath,
        certification_document_name: fileName
      });

      return { filePath, fileName };
    } catch (error) {
      console.error('Error saving certification:', error);
      throw error;
    }
  };

  const handleCertificationSubmit = async (certifications) => {
    updateFormData({ ownerCertifications: certifications });
    setShowCertificationModal(false);
    await finalizeSignup(certifications);
  };

  const finalizeSignup = async (certifications) => {
    setServerError('');
    setIsSubmitting(true);

    try {
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName,
            role: formData?.accountType,
            phone: formData?.phone
          }
        }
      });

      if (authError) throw authError;

      const userId = authData?.user?.id;

      if (formData?.driverLicense) {
        const licenseData = await uploadDocument(formData?.driverLicense, 'driver-licenses', userId);
        if (licenseData) {
          await supabase?.from('driver_licenses')?.insert({
            user_id: userId,
            file_path: licenseData?.filePath,
            file_name: licenseData?.fileName
          });
        }
      }

      if (formData?.insuranceDocument) {
        const insuranceData = await uploadDocument(formData?.insuranceDocument, 'insurance-documents', userId);
        if (insuranceData) {
          await supabase?.from('insurance_verifications')?.insert({
            user_id: userId,
            file_path: insuranceData?.filePath,
            file_name: insuranceData?.fileName
          });
        }
      }

      if (formData?.accountType === 'owner' && certifications) {
        await saveCertificationDocument(userId, certifications);
      }

      // Notify admin of new signup (non-blocking)
      try {
        await sendAdminSignupNotification({
          userId,
          userName: formData?.fullName,
          userEmail: formData?.email,
          accountType: formData?.accountType,
          phone: formData?.phone
        });
      } catch (notifyErr) {
        console.warn('Admin signup notification failed (non-critical):', notifyErr);
      }

      navigate('/home-page');
    } catch (error) {
      setServerError(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (formData?.accountType === 'owner') {
      setShowCertificationModal(true);
    } else {
      await finalizeSignup(null);
    }
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/home-page" className="inline-flex items-center space-x-3 mb-6">
            <img 
              src="/assets/images/ChatGPT_Image_Feb_3_2026_04_58_59_PM-1770675660365.png" 
              alt="Draggin Trailers Logo" 
              className="w-12 h-12 object-contain rounded-md"
            />
            <span className="font-heading font-bold text-2xl text-foreground">Draggin Trailers</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Create Your Account</h1>
          <p className="font-body text-muted-foreground">Join our community of trailer owners and renters</p>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <React.Fragment key={step?.number}>
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-smooth ${
                      currentStep >= step?.number
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {currentStep > step?.number ? (
                      <Icon name="Check" size={20} strokeWidth={2.5} />
                    ) : (
                      <Icon name={step?.icon} size={20} strokeWidth={2} />
                    )}
                  </div>
                  <div className="hidden md:block">
                    <p className="font-body text-sm font-medium text-foreground">{step?.title}</p>
                  </div>
                </div>
                {index < steps?.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-4 rounded transition-smooth ${
                      currentStep > step?.number ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-elevation-3 p-8">
          {serverError && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-md flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" strokeWidth={2} />
              <p className="font-body text-sm text-error flex-1">{serverError}</p>
            </div>
          )}

          {currentStep === 1 && (
            <BasicInfoStep
              formData={formData}
              updateFormData={updateFormData}
              onNext={handleNext}
            />
          )}

          {currentStep === 2 && (
            <DocumentVerificationStep
              formData={formData}
              updateFormData={updateFormData}
              onBack={handleBack}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="font-body text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      <OwnerCertificationModal
        isOpen={showCertificationModal}
        onClose={() => setShowCertificationModal(false)}
        onSubmit={handleCertificationSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default Signup;